// 批次 20：教師名單安排 modal（main-dept-admin-main 頁專屬）。
//
// 鏡像 legacy：
//   - JSP：deptAdminMain.jsp:88-129（clinicalArrangementModal：#phase-period :98／#plan-clinical-amount :102／
//     #plan-target-id :103／#phaseClinicalTable :104（表頭 學員姓名 :107＋臨床教師1(請指定住院醫師)* :109＋臨床教師2(請指定
//     主治教師)* :112＋臨床教師3* :114）／#phase-clinical-save 儲存 :123）＋ mainPageTemplate.jsp:544-575
//     phase_clinical_tmpl（tr data-id=phaseInstId :545＋3 組 select clinical1Dept/clinical1Id data-default…）
//   - JS：deptAdminMain.js:133-149（開啟：clinicalAmount／plan-target-id／部門清單＋clinicalList →
//     renderPhaseClinicalTable＋「訓練期間：」＋phasePeriod）、:152-176（clinicalDept/clinicalId change →
//     setAccountListByDepartmentIdNew 鏡像＋tr.edited）、:180-200（儲存驗證鏈）、:256-302
//     （getValidPhaseList :256-308（return :307）／isValidClinical :315-320）、:348-388（renderPhaseClinicalTable：<=0 刪欄 :353-361、
//     clinicalAmount<3 隱第 4 欄、dept 預設 val=loginAccountDepartmentId＋trigger change）、
//     :325-346（getDepartmentListByOrgIdAndJobTypeId＝同步 AJAX，雛形以種子直讀）、
//     ePortfolio.js:1752-1770（setAccountListByDepartmentIdNew → ajax/getAccountListByDepartmentIdNewAjax，
//     雛形以種子直讀）
//   - select2/chosen 美化不鏡像（D20-6，原生 select）。
//
// 【證據等級】§7 第 3 級（原始碼）。種子值【雛形值待實機校準】。

import { useMemo, useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { ModalFrame, useDialogs } from '@/components/layouts';
import type {
  DeptAdminClinicalRow,
  DeptAdminPhase,
  DeptAdminPlanDto,
} from '@/mocks/dashboard-store';
import { kebab } from '../dashboard/dashboardParts';

export type DeptAdminAccount = { readonly id: string; readonly name: string };
export type DeptAdminDepartment = { readonly id: string; readonly name: string };

/** 帳號 id 是否有效（deptAdminMain.js:315-320 isValidClinical：非 undefined/null 且 >0；字串 '7'>0＝true）。 */
export function isValidClinical(value: string | number | null | undefined): boolean {
  if (typeof value === 'undefined' || value === null) return false;
  return Number(value) > 0;
}

/** 儲存 payload（deptAdminMain.js:256-308 getValidPhaseList：id／edited／clinicalId／clinicalId2／clinicalId3）。 */
export interface DeptAdminPhaseInstSave {
  readonly id: string;
  readonly edited: boolean;
  readonly clinicalId?: string;
  readonly clinicalId2?: string;
  readonly clinicalId3?: string;
}

export interface ClinicalArrangementModalProps {
  readonly pageId: string;
  readonly open: boolean;
  /** clinicalRow click 帶入（deptAdminMain.js:133-149）。 */
  readonly planTarget: DeptAdminPlanDto['planTarget'];
  readonly phaseData: DeptAdminPhase;
  /** main/ugy/deptAdmin/clinicalList 種子（key＝phaseData.phaseTargetId）。 */
  readonly clinicalRows: readonly DeptAdminClinicalRow[];
  readonly departments: readonly DeptAdminDepartment[];
  /** ajax/getAccountListByDepartmentIdNewAjax 種子（key＝departmentId）。 */
  readonly accounts: Readonly<Record<string, readonly DeptAdminAccount[]>>;
  readonly loginAccountDepartmentId: string;
  readonly onClose: () => void;
  /** updatePhaseClinical 鏡像（savePhaseInstList :226-250）；呼叫端負責 alert(resp.desc)＋重抓面板。 */
  readonly onSave: (phaseInstList: readonly DeptAdminPhaseInstSave[]) => void;
}

interface RowState {
  readonly phaseInstId: string;
  readonly studentName: string;
  readonly studentEmpId: string;
  /** 預設值（phase_clinical_tmpl data-default；初始還原即消費歸零＝F2；<=0 已被
   *  renderPhaseClinicalTable :353-361 刪除）。 */
  readonly default1: string;
  readonly default2: string;
  readonly default3: string;
  /** 三個部門 select 的值（初始＝loginAccountDepartmentId，:355）。 */
  readonly dept1: string;
  readonly dept2: string;
  readonly dept3: string;
  /** 三個帳號 select 的值。 */
  readonly v1: string;
  readonly v2: string;
  readonly v3: string;
  readonly edited: boolean;
}

/** 列初始狀態（renderPhaseClinicalTable :348-388：<=0 的 clinicalNId 刪除＝不還原預設、
 *  dept 預設 loginAccountDepartmentId＋trigger change → 帳號 select 還原 data-default（若在清單內）。
 *  **F2 修（一次性消費）**：legacy 初始 trigger("change") 的 callback 還原後即 removeData("default")
 *  （deptAdminMain.js:159-166）→ 後續部門切換不再還原；因此 defaultN 在初始還原後即歸零（＝已消費）。 */
function toRowState(
  row: DeptAdminClinicalRow,
  loginAccountDepartmentId: string,
  accounts: Readonly<Record<string, readonly DeptAdminAccount[]>>,
): RowState {
  const d1 = row.clinical1Id > 0 ? String(row.clinical1Id) : '';
  const d2 = row.clinical2Id > 0 ? String(row.clinical2Id) : '';
  const d3 = row.clinical3Id > 0 ? String(row.clinical3Id) : '';
  const inLoginList = (defaultValue: string) =>
    defaultValue !== '' &&
    (accounts[loginAccountDepartmentId]?.some((account) => account.id === defaultValue) ?? false);
  return {
    phaseInstId: row.phaseInstId,
    studentName: row.studentName,
    studentEmpId: row.studentEmpId,
    default1: '',
    default2: '',
    default3: '',
    dept1: loginAccountDepartmentId,
    dept2: loginAccountDepartmentId,
    dept3: loginAccountDepartmentId,
    // 初始還原＝第一次 trigger("change") 語意（消费 default；F2）。
    v1: inLoginList(d1) ? d1 : '',
    v2: inLoginList(d2) ? d2 : '',
    v3: inLoginList(d3) ? d3 : '',
    edited: false,
  };
}

export function ClinicalArrangementModal({
  pageId,
  open,
  planTarget,
  phaseData,
  clinicalRows,
  departments,
  accounts,
  loginAccountDepartmentId,
  onClose,
  onSave,
}: ClinicalArrangementModalProps): ReactElement {
  const dialogs = useDialogs();
  const [rows, setRows] = useState<readonly RowState[]>(() =>
    clinicalRows.map((row) => toRowState(row, loginAccountDepartmentId, accounts)),
  );
  const clinicalAmount = planTarget.clinicalAmount;
  const showThird = clinicalAmount >= 3;

  /**
   * 帳號 select 還原規則（ePortfolio.js:1762 setOptionList＋callback deptAdminMain.js:159-166：
   * default>0 才 val(defaultId)＋**removeData("default") 一次性消費**）。F2 修：初始還原已在
   * toRowState 消費（defaultN 歸零），後續部門切換恆不還原＝本函式對已消費的列恆回 ''（legacy 等價：
   * data-default 已移除，callback 內 defaultId undefined）。val 對不存在的 option 是 no-op＝留首欄
   * （請選擇）——「在清單內才還原」檢查與該行為等價（F4 備註）。
   */
  const accountValueOf = (row: RowState, column: 1 | 2 | 3): string => {
    const deptValue = column === 1 ? row.dept1 : column === 2 ? row.dept2 : row.dept3;
    const defaultValue = column === 1 ? row.default1 : column === 2 ? row.default2 : row.default3;
    const list = accounts[deptValue] ?? [];
    return isValidClinical(defaultValue) && list.some((account) => account.id === defaultValue)
      ? defaultValue
      : '';
  };

  const updateRow = (phaseInstId: string, patch: (row: RowState) => RowState): void => {
    setRows((current) => current.map((row) => (row.phaseInstId === phaseInstId ? patch(row) : row)));
  };

  /** 部門異動（deptAdminMain.js:152-170：tr.edited＋帳號選項重載＋請選擇 prepend＋default 一次性還原）。 */
  const onDeptChange = (phaseInstId: string, column: 1 | 2 | 3, deptValue: string): void => {
    updateRow(phaseInstId, (row) => {
      const merged = { ...row, edited: true };
      if (column === 1) {
        return { ...merged, dept1: deptValue, v1: accountValueOf({ ...row, dept1: deptValue }, 1) };
      }
      if (column === 2) {
        return { ...merged, dept2: deptValue, v2: accountValueOf({ ...row, dept2: deptValue }, 2) };
      }
      return { ...merged, dept3: deptValue, v3: accountValueOf({ ...row, dept3: deptValue }, 3) };
    });
  };

  /** 人員異動（deptAdminMain.js:172-176：tr.edited）。 */
  const onAccountChange = (phaseInstId: string, column: 1 | 2 | 3, value: string): void => {
    updateRow(phaseInstId, (row) => {
      if (column === 1) return { ...row, v1: value, edited: true };
      if (column === 2) return { ...row, v2: value, edited: true };
      return { ...row, v3: value, edited: true };
    });
  };

  /**
   * 儲存驗證鏈（deptAdminMain.js:180-200＋getValidPhaseList :256-308）：全列依 clinicalAmount
   * 驗證 → 無效列 alert「尚有學員未完成 …」／全有效→取 edited 列（無→info「無異動人員！」）。
   */
  const save = (): void => {
    const invalid = rows.some((row) => {
      if (clinicalAmount >= 1 && !isValidClinical(row.v1)) return true;
      if (clinicalAmount >= 2 && !isValidClinical(row.v2)) return true;
      if (clinicalAmount >= 3 && !isValidClinical(row.v3)) return true;
      return false;
    });
    if (invalid) {
      let errMsg = '尚有學員未完成 ';
      if (clinicalAmount >= 1) errMsg += '臨床教師1';
      if (clinicalAmount >= 2) errMsg += '、臨床教師2';
      if (clinicalAmount >= 3) errMsg += '、臨床教師3';
      errMsg += '的名單安排，請安排完成再存檔。';
      void dialogs.alert(errMsg);
      return;
    }
    const edited = rows
      .filter((row) => row.edited)
      .map((row) => ({
        id: row.phaseInstId,
        edited: true,
        ...(clinicalAmount >= 1 ? { clinicalId: row.v1 } : {}),
        ...(clinicalAmount >= 2 ? { clinicalId2: row.v2 } : {}),
        ...(clinicalAmount >= 3 ? { clinicalId3: row.v3 } : {}),
      }));
    if (edited.length === 0) {
      // deptAdminMain.js:188 showInfoDialog('無異動人員！')。
      void dialogs.info('無異動人員！');
      return;
    }
    onSave(edited);
  };

  const requiredMark = <span className="text-[#d9534f]">*</span>;
  const tableHead = useMemo(
    () => (
      <thead>
        <tr>
          <th data-ui-id={`${pageId}-clinical-col-name`} className="p-2 text-center font-bold">
            學員姓名
          </th>
          <th data-ui-id={`${pageId}-clinical-col-clinical-1`} className="p-2 text-center font-bold">
            {requiredMark} 臨床教師1(請指定住院醫師)
          </th>
          <th data-ui-id={`${pageId}-clinical-col-clinical-2`} className="p-2 text-center font-bold">
            {requiredMark}臨床教師2(請指定主治教師)
          </th>
          {showThird ? (
            <th data-ui-id={`${pageId}-clinical-col-clinical-3`} className="p-2 text-center font-bold">
              {requiredMark}臨床教師3
            </th>
          ) : null}
        </tr>
      </thead>
    ),
    // requiredMark 是常數節點；showThird/pageId 為唯一語意依賴。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageId, showThird],
  );

  return (
    <ModalFrame
      uiId={`${pageId}-clinical-arrangement-modal`}
      open={open}
      title="教師名單安排"
      onClose={onClose}
      size="lg"
      footer={
        <Button uiId={`${pageId}-clinical-save`} variant="primary" onClick={() => save()}>
          儲存
        </Button>
      }
    >
      <div className="space-y-3">
        <p data-ui-id={`${pageId}-clinical-period`} className="text-[14px]">
          訓練期間：{phaseData.phasePeriod}
        </p>
        <table data-ui-id={`${pageId}-clinical-table`} className="w-full border-collapse text-ink">
          {tableHead}
          <tbody>
            {rows.map((row) => {
              const rowKey = kebab(row.phaseInstId);
              // 無效列（phase-invalid；getValidPhaseList :283-287——驗證失敗後的殘留提示由
              // state 呈現：本批以 alert 呈現驗證訊息，列不高亮＝簡化鏡像，D20-6）。
              const rowInvalid =
                (clinicalAmount >= 1 && !isValidClinical(row.v1)) ||
                (clinicalAmount >= 2 && !isValidClinical(row.v2)) ||
                (clinicalAmount >= 3 && !isValidClinical(row.v3));
              return (
                <tr
                  key={row.phaseInstId}
                  data-ui-id={`${pageId}-clinical-row-${rowKey}`}
                  data-edited={row.edited ? 'true' : 'false'}
                  className={rowInvalid ? 'bg-[#fcf8e3]' : undefined}
                >
                  <td data-ui-id={`${pageId}-clinical-row-${rowKey}-student`} className="border border-border p-2 text-center">
                    {row.studentName}({row.studentEmpId})
                  </td>
                  {([1, 2, 3] as const).map((column) =>
                    column === 3 && !showThird ? null : (
                      <td
                        key={column}
                        data-ui-id={`${pageId}-clinical-row-${rowKey}-cell-${column}`}
                        className="border border-border p-2"
                      >
                        <div className="flex flex-col gap-1">
                          <select
                            data-ui-id={`${pageId}-clinical-row-${rowKey}-dept-${column}`}
                            name={`clinical${column}Dept`}
                            className="border border-border bg-white p-1 text-[13px]"
                            value={column === 1 ? row.dept1 : column === 2 ? row.dept2 : row.dept3}
                            onChange={(event) => onDeptChange(row.phaseInstId, column, event.target.value)}
                          >
                            {departments.map((department) => (
                              <option key={department.id} value={department.id}>
                                {department.name}
                              </option>
                            ))}
                          </select>
                          <select
                            data-ui-id={`${pageId}-clinical-row-${rowKey}-account-${column}`}
                            name={`clinical${column}Id`}
                            className="border border-border bg-white p-1 text-[13px]"
                            value={column === 1 ? row.v1 : column === 2 ? row.v2 : row.v3}
                            onChange={(event) => onAccountChange(row.phaseInstId, column, event.target.value)}
                          >
                            {/* 請選擇 prepend（deptAdminMain.js:161-164）；帳號清單＝setAccountListByDepartmentIdNew
                                鏡像（ePortfolio.js:1752-1770，種子直讀 D20-6）。legacy 的 option attr 寫「disable」
                                （非「disabled」＝無效 attr，請選擇實際可回選；legacy-known-issue 照實，F3 備註）——
                                實作以 value='' 可回選等價。 */}
                            <option value="">請選擇</option>
                            {(accounts[column === 1 ? row.dept1 : column === 2 ? row.dept2 : row.dept3] ?? []).map(
                              (account) => (
                                <option key={account.id} value={account.id}>
                                  {account.name}
                                </option>
                              ),
                            )}
                          </select>
                        </div>
                      </td>
                    ),
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ModalFrame>
  );
}
